import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from "framer-motion";
import { Users, TrendingUp } from "lucide-react";
import { useServerStatus } from "@/hooks/useServerStatus";

const Counter = ({ value }) => {
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { stiffness: 60, damping: 18 });
  const text = useTransform(spring, (v) => Math.round(v).toString());
  const ref = useRef(null);
  useEffect(() => { mv.set(value); }, [value, mv]);
  useEffect(() => text.on("change", (v) => { if (ref.current) ref.current.textContent = v; }), [text]);
  return <span ref={ref} data-testid="status-players">0</span>;
};

export const ServerStatus = ({ className = "" }) => {
  const status = useServerStatus();
  const online = status?.online;

  return (
    <div
      data-testid="server-status-pill"
      className={`inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2.5 font-mono text-xs sm:text-sm backdrop-blur-md ${className}`}
    >
      <span className="relative flex h-2 w-2">
        {online && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--glow)] opacity-60" />}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${online ? "bg-[var(--glow)]" : status ? "bg-red-500" : "bg-white/30"}`} />
      </span>
      <Users size={14} className="text-white/50" />
      <AnimatePresence mode="wait">
        {!status ? (
          <motion.span key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-white/50" data-testid="status-loading">
            Lekérdezés…
          </motion.span>
        ) : online ? (
          <motion.span key="online" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-white" data-testid="status-online">
            <Counter value={status.players} />
            <span className="text-white/40"> / {status.maxplayers}</span>
            <span className="ml-2 text-white/60">online</span>
            {typeof status.peak_today === "number" && (
              <span className="ml-3 inline-flex items-center gap-1.5 border-l border-white/15 pl-3 text-white/70" data-testid="status-peak" title="Mai csúcs">
                <TrendingUp size={13} className="text-[var(--glow)]" />
                <span className="text-white">{status.peak_today}</span>
                <span className="text-white/45">ma</span>
              </span>
            )}
          </motion.span>
        ) : (
          <motion.span key="offline" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-white/60" data-testid="status-offline">
            Offline
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
};
